import React, { useState } from 'react';
import { usePlaybook } from '../../context/PlaybookContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const BattlecardManager = ({ projectId }) => {
  const { battlecards, createStandardBattlecardPack, updateBattlecard } = usePlaybook();
  const { currentUser } = useCRM();
  const [selectedCardId, setSelectedCardId] = useState(null);

  const projectCards = battlecards.filter(b => b.project_id === projectId);
  const selectedCard = projectCards.find(c => c.id === selectedCardId);

  const getCardIcon = (name) => {
    const n = name.toLowerCase();
    if (n.includes('billig')) return '💰';
    if (n.includes('intern')) return '🏗️';
    if (n.includes('status quo')) return '🛑';
    if (n.includes('konsulent')) return '👔';
    return '⚔️';
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-xl font-black text-slate-900">Battlecards</h3>
          <p className="text-sm text-slate-500">Konkurranseanalyse og håndtering av innvendinger.</p>
        </div>
        {projectCards.length === 0 && currentUser.role !== 'Customer_Viewer' && (
          <button 
            onClick={() => createStandardBattlecardPack(projectId)}
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
          >
            <SafeIcon icon="⚔️" /> Opprett Standard Pack (V1.1)
          </button>
        )}
      </div>

      {projectCards.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* List Column */}
          <div className="lg:col-span-1 space-y-3">
            {projectCards.map(card => (
              <div 
                key={card.id} 
                onClick={() => setSelectedCardId(card.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4 ${selectedCardId === card.id ? 'bg-primary-50 border-primary-600 shadow-md transform scale-[1.02]' : 'bg-white border-slate-200 hover:border-primary-200 hover:bg-slate-50'}`}
              >
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-xl shadow-sm border border-slate-100">
                  <SafeIcon icon={getCardIcon(card.competitor_name)} />
                </div>
                <div>
                  <h4 className={`font-bold text-sm ${selectedCardId === card.id ? 'text-primary-900' : 'text-slate-700'}`}>{card.competitor_name}</h4>
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider line-clamp-1">{card.when_it_comes_up}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Detail Column */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {selectedCard ? (
                <motion.div 
                  key={selectedCard.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden"
                >
                  <div className="bg-slate-50 px-8 py-6 border-b border-slate-200 flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-black text-slate-900">{selectedCard.competitor_name}</h2>
                      <p className="text-sm text-slate-500 mt-1 font-medium">{selectedCard.when_it_comes_up}</p>
                    </div>
                    <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-100">
                      <SafeIcon icon={getCardIcon(selectedCard.competitor_name)} className="text-2xl" />
                    </div>
                  </div>
                  
                  <div className="p-8 space-y-8">
                    {/* Positioning */}
                    <div className="p-6 bg-indigo-50 border border-indigo-100 rounded-2xl relative">
                      <div className="absolute top-0 right-0 p-4 opacity-10">
                        <SafeIcon icon="🎯" className="text-6xl text-indigo-900" />
                      </div>
                      <h4 className="text-xs font-black text-indigo-900 uppercase tracking-widest mb-2">Vår Posisjonering</h4>
                      <p className="text-lg font-bold text-indigo-800 leading-relaxed">"{selectedCard.positioning_vs}"</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Strengths / Weaknesses */}
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <SafeIcon icon="💪" className="text-emerald-500" /> Deres Styrker
                          </h4>
                          <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100">{selectedCard.strengths}</p>
                        </div>
                        <div>
                          <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <SafeIcon icon="📉" className="text-rose-500" /> Deres Svakheter
                          </h4>
                          <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100">{selectedCard.weaknesses}</p>
                        </div>
                      </div>

                      {/* Objections / Responses */}
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <SafeIcon icon="🗣️" className="text-amber-500" /> Du hører
                          </h4>
                          <p className="text-sm italic text-amber-800 bg-amber-50 p-3 rounded-xl border border-amber-100">"{selectedCard.objections_you_hear}"</p>
                        </div>
                        <div>
                          <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <SafeIcon icon="💬" className="text-blue-500" /> Beste Svar
                          </h4>
                          <p className="text-sm font-bold text-blue-900 bg-blue-50 p-3 rounded-xl border border-blue-100">"{selectedCard.best_responses}"</p>
                        </div>
                      </div>
                    </div>

                    {/* Landmines */}
                    <div className="pt-6 border-t border-slate-100">
                      <h4 className="text-xs font-black text-rose-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <SafeIcon icon="💣" /> Landminer (Unngå dette)
                      </h4>
                      <p className="text-sm text-slate-600">{selectedCard.landmines}</p>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-12 text-center bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                  <SafeIcon icon="👈" className="w-16 h-16 text-slate-300 mb-6" />
                  <h3 className="text-lg font-black text-slate-900">Velg et kort</h3>
                  <p className="text-slate-500 max-w-xs mx-auto mt-2">Klikk på en konkurrent i listen til venstre for å se detaljer.</p>
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      ) : (
        <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-[2rem]">
          <SafeIcon icon="⚔️" className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-900">Ingen battlecards definert</h3>
          <p className="text-slate-500 mb-6">Gi teamet ammunisjon mot konkurrentene.</p>
          {currentUser.role !== 'Customer_Viewer' && (
            <button 
              onClick={() => createStandardBattlecardPack(projectId)}
              className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 shadow-lg"
            >
              Opprett Standard Pack
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BattlecardManager;